import dayjs from 'dayjs';
import { getV7Now } from '@/api-client/common/queries/mode-controller/mode-controller';
import { utcZeroTimeToStandardTime, toTimezoneKeepingWallClock } from '@/common/utils/browser';
import {
  GLOBAL_TIME_LIVE_FORMAT,
  GLOBAL_TIME_RANGE_FORMAT,
} from '@/common/utils/core/time/constants';
import { returnMatchingFormat, parsePeriod } from '@/common/utils/core/time/parse';
import { getSafeDateTime } from '@/common/utils/core/time/calculate';
import { createTimePeriodInfoFromCurrentTime } from '@/common/components/molecules/timeController/utils';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { useMyInfoStore } from '@/config/stores/my-info';

/**
 * /v7/now fetch 결과(UTC ISO)를 짧은 시간 동안 재사용한다.
 *
 * - 같은 macrotask 에서 다수의 호출자(예: 슬라이드 안의 LiveUpdater N 개)가 동시에 호출하면
 *   in-flight Promise 를 공유해 fetch 1 회로 합쳐진다.
 * - TTL 안에 들어온 후속 호출(예: handleResume 직후 LiveUpdater watcher 의 두 번째 호출)도
 *   직전 결과를 재사용한다. TTL 을 너무 길게 잡으면 stale 한 base time 이 다음 워커 setBase 에
 *   주입되므로, 라이브 주기(5 초) 보다 충분히 짧은 500 ms 로 잡는다.
 */
const CACHE_TTL_MS = 500;
let inFlightFetch: Promise<string | null> | null = null;
let lastFetched: { utcTime: string | null; at: number } | null = null;

const fetchServerUtcTime = (): Promise<string | null> => {
  if (lastFetched && Date.now() - lastFetched.at < CACHE_TTL_MS) {
    return Promise.resolve(lastFetched.utcTime);
  }
  if (inFlightFetch) return inFlightFetch;

  inFlightFetch = (async () => {
    try {
      const response = await getV7Now();
      const time = response.data.data;
      const utcTime = time != null ? new Date(time).toISOString() : null;
      lastFetched = { utcTime, at: Date.now() };
      return utcTime;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      inFlightFetch = null;
    }
  })();

  return inFlightFetch;
};

/**
 * 서버에서 현재 시간을 가져오는 API 함수
 * @param timeFormat - 반환할 시간 형식 (기본값: GLOBAL_TIME_LIVE_FORMAT)
 * @returns 서버 현재 시간 문자열
 */
export const getServerCurrentTime = async ({
  timeFormat = GLOBAL_TIME_LIVE_FORMAT,
}: {
  timeFormat?: string;
} = {}) => {
  const utcTime = await fetchServerUtcTime();
  if (utcTime == null) {
    return dayjs.tz().format(timeFormat);
  }
  return utcZeroTimeToStandardTime(utcTime, timeFormat);
};

/**
 * 서버 현재 시간과 비교하여 시간이 초과되었는지 확인
 * @param time - 비교할 시간 문자열
 * @returns 시간이 초과되었는지 여부
 */
export const isExceededServerCurrentTime = async ({ time }: { time: string }) => {
  const currentTime = await getServerCurrentTime();
  const { timezone: tz } = useMyInfoStore();

  // 양변 모두 계정 tz 기준 wall clock 문자열이므로 같은 경로로 instant 를 만든다.
  // `dayjs(currentTime).tz()` 는 계정 tz 문자열을 브라우저 로컬로 파싱해 두 값의 프레임이
  // 어긋났다 (브라우저 tz != 계정 tz 이면 그 offset 차이만큼 오판).
  return (
    toTimezoneKeepingWallClock(dayjs(time, returnMatchingFormat(time)), tz).unix() >
    toTimezoneKeepingWallClock(dayjs(currentTime, returnMatchingFormat(currentTime)), tz).unix()
  );
};

/**
 * 서버 시간을 기준으로 현재 시간 범위를 가져옴
 * @param period - 기간 문자열
 * @param timeFormat - 시간 형식
 * @returns 시간 범위 객체
 */
export const getCurrentTimeRangeByServerTime = async ({
  period,
  timeFormat = GLOBAL_TIME_RANGE_FORMAT,
}: {
  period: string;
  timeFormat?: string;
}) => {
  const { offset, unit } = parsePeriod(period);
  const currentTime = await getServerCurrentTime();

  const fromTime = getSafeDateTime(dayjs(currentTime).subtract(offset, unit)).format(timeFormat);
  const toTime = dayjs(currentTime).format(timeFormat);

  return {
    fromTime,
    toTime,
  };
};

/**
 * 서버 시간을 기준으로 초기 TimePeriodInfo 생성
 * @param isPaused - 일시정지 여부
 * @param timePeriod - 기간 문자열
 * @param timeFormat - 시간 형식
 * @returns TimePeriodInfo
 */
export const getInitPeriodInfoByServerTime = async ({
  isPaused,
  timePeriod,
  timeFormat,
}: Partial<TimePeriodInfo> & { timeFormat?: string } = {}): Promise<TimePeriodInfo> => {
  const currentTime = await getServerCurrentTime();

  return createTimePeriodInfoFromCurrentTime({
    currentTime,
    timePeriod: timePeriod ?? 'p10m',
    isPaused: isPaused ?? false,
    timeFormat,
  });
};
